import {AutoRefreshButton} from "@/components/autoRefreshButton/AutoRefreshButton.tsx";
import {IconButton} from "@/components/iconButton/IconButton.tsx";
import {NavTabs} from "@/components/navTabs/NavTabs.tsx";
import classNames from "classnames";
import {FiMoon, FiSun} from "react-icons/fi";
import {useAutoRefresh} from "../hooks/useAutoRefresh.ts";
import {StatusPageTab} from "../types";
import styles from "./StatusPageHeader.module.scss";
import {SubscribeDropdown} from "./subscribeDropdown/SubscribeDropdown.tsx";

const ALL_TAB_ITEMS: {id: StatusPageTab; title: string; configKey: "showStatusTab" | "showHistoryTab"}[] = [
  {id: StatusPageTab.STATUS, title: "Current Status", configKey: "showStatusTab"},
  {id: StatusPageTab.HISTORY, title: "History", configKey: "showHistoryTab"},
];

interface StatusPageHeaderProps {
  logoUrl: string | null;
  pageName: string;
  pageDescription: string | null;
  showStatusTab: boolean;
  showHistoryTab: boolean;
  allowSubscriptions: boolean;
  allowSubscriptionsEmail: boolean;
  allowSubscriptionsSms: boolean;
  allowSubscriptionsRss: boolean;
  allowSubscriptionsSlack: boolean;
  allowSubscriptionsWebhook: boolean;
  slackOAuthUrl: string | null;
  slug: string;
  activeTab: StatusPageTab;
  onTabChange: (tab: StatusPageTab) => void;
  onRefresh: () => void;
  autoRefreshPageKey: string | undefined;
  isDarkMode: boolean;
  onToggleDarkMode: () => void;
  headerBgColor: string | null;
  headerTextColor: string | null;
}

export const StatusPageHeader = ({
  logoUrl,
  pageName,
  pageDescription,
  showStatusTab,
  showHistoryTab,
  allowSubscriptions,
  allowSubscriptionsEmail,
  allowSubscriptionsSms,
  allowSubscriptionsRss,
  allowSubscriptionsSlack,
  allowSubscriptionsWebhook,
  slackOAuthUrl,
  slug,
  activeTab,
  onTabChange,
  onRefresh,
  autoRefreshPageKey,
  isDarkMode,
  onToggleDarkMode,
  headerBgColor,
  headerTextColor,
}: StatusPageHeaderProps) => {
  const tabConfig = {showStatusTab, showHistoryTab};
  const visibleTabs = ALL_TAB_ITEMS.filter((tab) => tabConfig[tab.configKey]);

  const {interval, progressPercent, setAutoRefreshInterval, refreshNow} = useAutoRefresh({
    onRefresh,
    pageKey: autoRefreshPageKey,
  });

  return (
    <>
      <header
        className={classNames(styles.header, {[styles.customTextColor]: headerTextColor})}
        style={
          {
            ...(headerBgColor ? {backgroundColor: headerBgColor, "--sp-header-bg": headerBgColor} : {}),
            ...(headerTextColor ? {color: headerTextColor, "--sp-header-text": headerTextColor} : {}),
          } as React.CSSProperties
        }
      >
        <div className={styles.left}>
          {logoUrl ? (
            <img src={logoUrl} alt={pageName} className={styles.logo} />
          ) : (
            <div className={styles.nameBlock}>
              <span className={styles.pageName}>{pageName}</span>
              {pageDescription && <span className={styles.pageDescription}>{pageDescription}</span>}
            </div>
          )}
        </div>

        {visibleTabs.length > 1 && (
          <nav className={styles.center}>
            <NavTabs
              items={visibleTabs}
              selectedId={activeTab}
              selectTab={(tab) => onTabChange(tab as StatusPageTab)}
              variant="statuspage"
            />
          </nav>
        )}

        <div className={styles.right}>
          <div className={styles.iconGroup}>
            <IconButton
              icon={isDarkMode ? FiSun : FiMoon}
              variant="ghost"
              size="fitIcon"
              className={styles.iconBtn}
              onClick={onToggleDarkMode}
              title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
              aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
            />
            <AutoRefreshButton
              seconds={interval > 0 ? interval * 60 : undefined}
              setSeconds={(s) => setAutoRefreshInterval(s ? ((s / 60) as typeof interval) : 0)}
              doRefresh={refreshNow}
              size="fitIcon"
              variant="ghost"
              progressPercent={progressPercent}
              showActiveState
              className={styles.iconBtn}
            />
          </div>
          {allowSubscriptions && (
            <div className={classNames({[styles.subscribeInverted]: headerTextColor})}>
              <SubscribeDropdown
                slug={slug}
                allowEmail={allowSubscriptionsEmail}
                allowSms={allowSubscriptionsSms}
                allowRss={allowSubscriptionsRss}
                allowSlack={allowSubscriptionsSlack}
                allowWebhook={allowSubscriptionsWebhook}
                slackOAuthUrl={slackOAuthUrl}
              />
            </div>
          )}
        </div>
      </header>
    </>
  );
};
